import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { BookOpen, Users, UserCircle, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
    const { getAuthHeaders } = useAuth();
    const [stats, setStats] = useState({
        stories: 0,
        authors: 0,
        users: 0,
        activeSubscriptions: 0
    });
    const [recentActivity, setRecentActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [storiesRes, authorsRes, usersRes, auditRes] = await Promise.all([
                    axios.get(`${API}/admin/stories`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/admin/authors`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
                    axios.get(`${API}/admin/audit-log`, { headers: getAuthHeaders() })
                ]);

                setStats({
                    stories: storiesRes.data.length,
                    authors: authorsRes.data.length,
                    users: usersRes.data.length,
                    activeSubscriptions: usersRes.data.filter(u => 
                        u.subscription_status === 'active' || u.subscription_status === 'trialing'
                    ).length
                });

                setRecentActivity(auditRes.data.slice(0, 5));
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [getAuthHeaders]);

    const statCards = [
        { label: 'Povídky', value: stats.stories, icon: BookOpen, color: 'text-blue-500' },
        { label: 'Autoři', value: stats.authors, icon: UserCircle, color: 'text-purple-500' },
        { label: 'Uživatelé', value: stats.users, icon: Users, color: 'text-green-500' },
        { label: 'Aktivní předplatná', value: stats.activeSubscriptions, icon: TrendingUp, color: 'text-amber-500' },
    ];

    return (
        <div data-testid="admin-dashboard">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold mb-8">
                Přehled administrace
            </h1>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {statCards.map((stat, index) => (
                    <div 
                        key={stat.label}
                        className="listarna-card rounded-lg p-6"
                        data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <stat.icon className={`w-8 h-8 ${stat.color}`} />
                        </div>
                        <p className="text-3xl font-heading font-semibold mb-1">
                            {loading ? '...' : stat.value}
                        </p>
                        <p className="text-sm text-muted-foreground">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Recent Activity */}
            <div className="listarna-card rounded-lg p-6">
                <h2 className="font-heading text-xl font-medium mb-6">
                    Nedávná aktivita
                </h2>

                {loading ? (
                    <div className="space-y-3">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="animate-pulse flex gap-4">
                                <div className="h-4 bg-secondary rounded w-24" />
                                <div className="h-4 bg-secondary rounded flex-1" />
                            </div>
                        ))}
                    </div>
                ) : recentActivity.length === 0 ? (
                    <p className="text-muted-foreground">Zatím žádná aktivita</p>
                ) : (
                    <div className="space-y-3">
                        {recentActivity.map((activity) => (
                            <div 
                                key={activity.id}
                                className="flex items-center gap-4 py-2 border-b border-border last:border-0"
                            >
                                <span className="text-xs text-muted-foreground w-32">
                                    {new Date(activity.timestamp).toLocaleString('cs-CZ')}
                                </span>
                                <span className="px-2 py-0.5 bg-secondary rounded text-xs capitalize">
                                    {activity.action}
                                </span>
                                <span className="text-sm">
                                    {activity.entity_type}: {activity.entity_id.slice(0, 8)}...
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
